import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { proxied } from '../../services/assets';
import { audioEngine } from '../../services/audioEngine';
import { useAvatarChat } from '../../hooks/useAvatarChat';
import useAvatarVoiceChat from '../../hooks/useAvatarVoiceChat';
import useLiveAvatarSession from '../../hooks/useLiveAvatarSession';
import ChoiceChips from '../ChoiceChips';

// Contacts — CHAT MODE on the phone, in three grades of presence.
//
// The world has two kinds of conversation and the split is by CAUSE, not by
// app: Calls runs SCENES (a directed event — the dialogue director), Contacts
// runs CHAT (anyone you feel like talking to — ChatService). An active scene
// always wins the surface.
//
// Within chat, the three buttons on a contact are the same conversation at
// different levels of presence, on one thread:
//
//   💬 chat  — text. POST /api/chat/message.
//   📞 voice — the ElevenLabs conversational agent, streaming both ways.
//   📹 face  — the live Anam avatar, 9:16, in the phone.
//
// Text and voice write to the SAME rows the website's chat writes, so an artist
// you talked to on hypetunes.com remembers you here, mid-sentence.
const MODES = [
  { key: 'chat', icon: '💬', label: 'Message', accent: '#7dd3ff' },
  { key: 'voice', icon: '📞', label: 'Call', accent: '#7dff9d' },
  { key: 'face', icon: '📹', label: 'Video call', accent: '#ff9dc4' },
];

export default function PhoneContacts() {
  const npcList = useWorldStore((s) => s.npcList);
  const [who, setWho] = useState(null);      // { avatarId, name, headshotUrl }
  const [mode, setMode] = useState('chat');

  if (!who) {
    const people = (npcList || []).filter((n) => n.avatarId);
    return (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 12px 14px' }}>
        {people.length === 0 ? (
          <div style={{ font: '600 12.5px system-ui', opacity: 0.45, lineHeight: 1.6, marginTop: 30, textAlign: 'center' }}>
            Nobody in town right now.
          </div>
        ) : people.map((n) => (
          <div key={n.avatarId} style={row}>
            <Face name={n.name} url={n.headshotUrl} size={36} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', font: '700 13px system-ui', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.name}</span>
              <span style={{ display: 'block', font: '600 10px system-ui', opacity: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.genre || 'in HypeTown'}</span>
            </span>
            <span style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
              {MODES.map((m) => (
                <button
                  key={m.key}
                  onClick={() => { setWho(n); setMode(m.key); }}
                  title={`${m.label} ${n.name}`}
                  aria-label={`${m.label} ${n.name}`}
                  style={circle(m.accent)}
                >{m.icon}</button>
              ))}
            </span>
          </div>
        ))}
      </div>
    );
  }

  const back = () => setWho(null);
  if (mode === 'face') return <FaceCall who={who} onBack={back} />;
  if (mode === 'voice') return <VoiceCall who={who} onBack={back} />;
  return <TextThread who={who} onBack={back} />;
}

// ── 💬 text ─────────────────────────────────────────────────────────────────
function TextThread({ who, onBack }) {
  const { messages, input, setInput, send, sending, opening, choices, pick, note } =
    useAvatarChat({ avatarId: who.avatarId });
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages, sending]);

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CallHead who={who} onBack={onBack} />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {opening && <div style={{ opacity: 0.5, font: '600 12px system-ui', textAlign: 'center' }}>loading…</div>}
        {!opening && messages.length === 0 && (
          <div style={{ opacity: 0.45, font: '600 12px system-ui', textAlign: 'center', marginTop: 20, lineHeight: 1.6 }}>
            No history with {who.name} yet.<br />Say something.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.id || `local-${i}`} style={bubble(m.role === 'user', m.pendingLocal)}>
            {m.metadata?.attachment_url && (
              <img src={m.metadata.attachment_url} alt="" style={{ display: 'block', width: '100%', borderRadius: 9, marginBottom: 6 }} />
            )}
            {m.content}
          </div>
        ))}
        {sending && <div style={{ alignSelf: 'flex-start', opacity: 0.55, fontSize: 13 }}>…</div>}
        {/* Guided-flow buttons go through `pick`, not `send`: the server marks
            the set as taken atomically, so a double-tap can't answer twice. */}
        <ChoiceChips choices={choices} onPick={pick} disabled={sending} compact />
        {note && <div style={{ font: '600 11.5px system-ui', color: '#ffd27d', opacity: 0.9 }}>{note}</div>}
        <div ref={endRef} />
      </div>
      <div style={{ padding: '8px 10px 10px', display: 'flex', gap: 7 }}>
        <input
          value={input}
          placeholder={`Message ${who.name}…`}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          disabled={sending || opening}
          style={textInput}
        />
        <button onClick={() => send()} disabled={sending || opening} style={sendBtn}>Send</button>
      </div>
    </div>
  );
}

// ── 📞 voice ────────────────────────────────────────────────────────────────
// OPEN MIC, not push-to-talk. The agent does its own turn detection, and the
// mic pipe already sends silence rather than nothing while the floor is closed,
// so holding a button was never carrying any weight — it was just making the
// player do the VAD's job. Opening the screen is the gesture that arms it
// (iOS grants the mic and unlocks audio only inside one), and it stays open.
function VoiceCall({ who, onBack }) {
  const { messages } = useAvatarChat({ avatarId: who.avatarId });
  const { phase, caption, toggleTalk, end, micDenied, error } =
    useAvatarVoiceChat({ avatarId: who.avatarId });

  const armedRef = useRef(false);
  const [armed, setArmed] = useState(false);

  // Never auto-connect: a session costs money and iOS needs the gesture. One
  // deliberate tap opens the line, and from then on the mic simply stays live —
  // no holding, no re-arming between turns.
  const open = () => {
    if (armedRef.current) return;
    armedRef.current = true; setArmed(true);
    toggleTalk();
  };

  const lastSaid = [...messages].reverse().find((m) => m.role === 'assistant')?.content;

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CallHead who={who} onBack={onBack} phase={phase} />
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '16px 18px', textAlign: 'center' }}>
        <Face name={who.name} url={who.headshotUrl} size={92} ring={phase === 'listening' ? '#7dff9d' : phase === 'speaking' ? '#7dd3ff' : null} />
        <div>
          <div style={{ font: '800 18px system-ui' }}>{who.name}</div>
          <div style={{ font: '600 11.5px system-ui', opacity: 0.6, marginTop: 3 }}>
            {!armed ? 'Tap to open the line' : PHASE_LABEL[phase] || 'connected'}
          </div>
        </div>
        {(caption || lastSaid) && (
          <div style={{ font: '600 12.5px system-ui', opacity: 0.72, lineHeight: 1.5, maxHeight: 90, overflowY: 'auto' }}>
            {caption || lastSaid}
          </div>
        )}
        {micDenied && <div style={{ font: '600 11.5px system-ui', color: '#ffd27d' }}>Mic is blocked — use Message instead.</div>}
        {error && !micDenied && <div style={{ font: '600 11.5px system-ui', color: '#ff8a9c' }}>{error}</div>}
      </div>
      <div style={{ padding: '10px 0 16px', display: 'flex', justifyContent: 'center', gap: 22 }}>
        {!armed ? (
          <button onClick={open} style={bigCircle('#2fd06a')} aria-label="Open the line">📞</button>
        ) : (
          <button onClick={() => { end(); onBack(); }} style={bigCircle('#ff5065')} aria-label="Hang up">✕</button>
        )}
      </div>
    </div>
  );
}

// ── 📹 face ─────────────────────────────────────────────────────────────────
// The live avatar, framed 9:16 in the phone — a video call looks like a video
// call. The stream target has to exist in the DOM before start(), because the
// SDK attaches to it by id.
const FACE_VIDEO_ID = 'phone-live-video';

function FaceCall({ who, onBack }) {
  const { phase, message, minutes, setMinutes, costPerMinute, secondsLeft, start, endCall, reset } =
    useLiveAvatarSession({ avatarId: who.avatarId, videoElementId: FACE_VIDEO_ID });

  const live = phase === 'live';

  // The caller's face owns the mix for the whole call, not just while they talk.
  useEffect(() => {
    if (!live) return undefined;
    audioEngine.duck(true, true);
    return () => audioEngine.duck(false);
  }, [live]);

  // Leaving the screen must END the session, not merely disconnect it: endCall
  // is what reports the end and refunds the unused minutes, and the hook's own
  // unmount does only the teardown. Held in a ref so we always end the session
  // that is actually running rather than the one captured on first render.
  const endRef = useRef(endCall);
  endRef.current = endCall;
  useEffect(() => () => { endRef.current?.(); }, []);

  const mmss = secondsLeft != null
    ? `${String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:${String(secondsLeft % 60).padStart(2, '0')}`
    : null;

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CallHead who={who} onBack={onBack} phase={live ? 'speaking' : null} trailing={live && mmss ? mmss : null} />

      {/* THE FRAME ONLY EXISTS WHEN THERE IS A PICTURE IN IT.
          A 9:16 card with a dark fill is a video player; with no video it is a
          panel sitting on the phone for no reason, and the phone is already the
          frame. So the box — background, aspect ratio, the lot — belongs to the
          LIVE state, and everything before the call just sits on the screen.
          (It was also the source of the overflow: width-driven 9:16 on a 260px
          screen derives a ~460px height, taller than the screen, and a centred
          child spills equally above and below.) */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: live ? 0 : '10px 16px' }}>
        <div style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          ...(live
            ? { height: '100%', width: 'auto', maxWidth: '100%', aspectRatio: '9 / 16', background: '#0b0810', overflow: 'hidden' }
            : { width: '100%' }),
        }}>
          {/* Always mounted: streamToVideoElement() resolves this by id, so it
              cannot be conditional on `live` without racing the connect. Parked
              at 1px while idle rather than stretched behind the content, so it
              draws nothing and still exists to be found. */}
          <video
            id={FACE_VIDEO_ID}
            autoPlay
            playsInline
            style={live
              ? { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 1, transition: 'opacity 400ms ease' }
              : { position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
          {!live && (
            <div style={{ position: 'relative', textAlign: 'center', padding: 16 }}>
              <Face name={who.name} url={who.headshotUrl} size={76} />
              <div style={{ font: '800 15px system-ui', marginTop: 12 }}>{who.name}</div>
              <div style={{ font: '600 11.5px system-ui', opacity: 0.6, marginTop: 4, lineHeight: 1.5 }}>
                {message || (phase === 'connecting' ? 'Connecting…' : `${costPerMinute} credit${costPerMinute === 1 ? '' : 's'} a minute`)}
              </div>
              {phase === 'confirm' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 12 }}>
                  <button onClick={() => setMinutes(Math.max(1, minutes - 1))} style={stepBtn}>−</button>
                  <span style={{ font: '800 15px system-ui', minWidth: 54 }}>{minutes} min</span>
                  <button onClick={() => setMinutes(minutes + 1)} style={stepBtn}>+</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '10px 0 16px', display: 'flex', justifyContent: 'center', gap: 22 }}>
        {live ? (
          <button onClick={() => { endCall(); onBack(); }} style={bigCircle('#ff5065')} aria-label="End call">✕</button>
        ) : phase === 'confirm' ? (
          <button onClick={start} style={bigCircle('#2fd06a')} aria-label="Start video call">📹</button>
        ) : (phase === 'ended' || phase === 'error') ? (
          // A HUNG-UP CALL STILL HAS A PHONE. Without this the screen ended on a
          // sentence and no control at all — the only way out was to back out of
          // the contact, which reads as the feature being broken rather than the
          // call being over.
          <button onClick={reset} style={bigCircle('#2fd06a')} aria-label="Call again">📹</button>
        ) : null}
      </div>
    </div>
  );
}

// ── shared ──────────────────────────────────────────────────────────────────
function CallHead({ who, onBack, phase, trailing }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px 8px', borderBottom: '1px solid rgba(255,233,196,0.12)' }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ffb25e', font: '700 15px system-ui', padding: '2px 4px' }} aria-label="Back to contacts">‹</button>
      <span style={{ flex: 1, minWidth: 0, font: '700 13.5px system-ui', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{who.name}</span>
      {trailing && <span style={{ font: '700 10.5px system-ui', opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>{trailing}</span>}
      {phase && phase !== 'idle' && (
        <span style={{ font: '700 10px system-ui', color: PHASE_COLOR[phase] || '#ffd27d', whiteSpace: 'nowrap' }}>
          {PHASE_LABEL[phase] || ''}
        </span>
      )}
    </div>
  );
}

// Same fallback rule as the AppearAs picker: an artist with no headshot yet is
// still someone you can talk to.
function Face({ name, url, size, ring }) {
  const base = {
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    ...(ring ? { boxShadow: `0 0 0 3px ${ring}` } : {}),
  };
  if (url) return <img src={proxied(url)} alt="" style={{ ...base, objectFit: 'cover' }} />;
  return (
    <span style={{
      ...base, display: 'grid', placeItems: 'center',
      background: 'linear-gradient(160deg,#ffb25e,#ff5e78)', color: '#1a0f00',
      font: `800 ${Math.round(size * 0.42)}px system-ui`, textTransform: 'uppercase',
    }}>{(name || '?').trim().charAt(0)}</span>
  );
}

const PHASE_LABEL = { connecting: 'connecting', listening: 'listening', thinking: 'thinking', speaking: 'speaking', error: 'mic problem' };
const PHASE_COLOR = { connecting: '#ffd27d', listening: '#7dff9d', thinking: '#ffd27d', speaking: '#7dd3ff', error: '#ff8a9c' };

const row = {
  display: 'flex', alignItems: 'center', gap: 9,
  padding: '9px 2px', borderBottom: '1px solid rgba(255,233,196,0.1)', color: '#ffe9c4',
};
const circle = (accent) => ({
  width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', fontSize: 12,
  border: `1px solid ${accent}`, background: 'rgba(255,255,255,0.06)',
  display: 'grid', placeItems: 'center', padding: 0, lineHeight: 1,
});
const bigCircle = (bg) => ({
  width: 58, height: 58, borderRadius: '50%', border: 'none', cursor: 'pointer',
  background: bg, color: '#fff', fontSize: 21, display: 'grid', placeItems: 'center',
  boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
});
const stepBtn = {
  width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', fontSize: 15,
  border: '1px solid rgba(255,178,94,0.45)', background: 'rgba(255,178,94,0.12)',
  color: '#ffe9c4', display: 'grid', placeItems: 'center', padding: 0,
};
const bubble = (mine, faded) => ({
  alignSelf: mine ? 'flex-end' : 'flex-start',
  maxWidth: '88%', padding: '8px 11px', borderRadius: 14, fontSize: 13.5, lineHeight: 1.42,
  opacity: faded ? 0.6 : 1,
  background: mine ? 'rgba(255,178,94,0.2)' : 'rgba(255,255,255,0.08)',
  border: mine ? '1px solid rgba(255,178,94,0.35)' : '1px solid rgba(255,255,255,0.1)',
});
const textInput = {
  flex: 1, minWidth: 0, padding: '9px 11px', borderRadius: 999,
  border: '1px solid rgba(255,178,94,0.3)', background: 'rgba(255,255,255,0.07)',
  color: '#fff', fontSize: 13.5, fontFamily: 'inherit', outline: 'none',
};
const sendBtn = {
  padding: '0 15px', borderRadius: 999, border: 'none', cursor: 'pointer',
  font: '700 12.5px system-ui', background: 'linear-gradient(180deg,#ffb25e,#ff5e78)', color: '#1a0f00',
};
